import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { ThemeProvider, createTheme, CssBaseline, GlobalStyles } from '@mui/material';
import { paletteLight } from '../../theme/palette';
import { typography } from '../../theme/typography';
import { getTheme as getPremiumTheme } from '../../theme/theme';

/**
 * Light-only theme provider. Dark mode was removed in V1 because
 * VM Display is a commercial signage SaaS where mixed-theme chrome
 * leaked into customer-facing screenshots.
 *
 * `useThemeContext` still exposes `mode` and `toggleTheme` so any
 * legacy callers don't crash, but:
 *   - `mode` is hard-coded `"light"`
 *   - `toggleTheme` is a no-op
 *   - the previous `localStorage.themeMode` value is silently ignored.
 *     We don't actively remove the key — leaving it does no harm and
 *     avoids touching every existing user's localStorage on first
 *     load.
 */
interface ThemeContextType {
  mode: 'light';
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Build a single light theme by merging the premium navy + gold theme
// with the legacy palette (which carries custom tokens — mainMenuNav,
// topbarButton, cardAdd, heading, table — that the sidebar consumes).
const buildLightTheme = () => {
  const premium = getPremiumTheme('light');
  return createTheme({
    ...premium,
    palette: {
      ...paletteLight,     // custom tokens (mainMenuNav, topbarButton, etc.)
      ...premium.palette,  // navy/gold + light bg/text — premium wins
    },
    typography: {
      ...typography,
      ...premium.typography,
    },
    components: {
      ...premium.components,
    },
  });
};

export const ThemeProviderWrapper: React.FC<{ children: ReactNode }> = ({ children }) => {
  const theme = useMemo(() => buildLightTheme(), []);

  const value = useMemo<ThemeContextType>(() => ({
    mode: 'light',
    toggleTheme: () => { /* dark mode removed in V1 */ },
  }), []);

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            "*": { scrollbarWidth: "thin" },
            "*::-webkit-": { width: "1px" },
            "*::-webkit--track": { background: theme.palette.background.paper },
            "*::-webkit--thumb": {
              backgroundColor: theme.palette.primary.main,
              borderRadius: "10px",
            },
          }}
        />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProviderWrapper');
  }
  return context;
};
